import { useState, useEffect, useCallback } from 'react';
import type {
  DateRange,
  HistoricalJob,
  PricingSetting,
  DurationStats,
  CostStats,
  CompletionStats,
  ErrorCategory,
  EndpointStats,
  HistogramBucket,
  StatsData,
  VersionStats,
  FalEndpointUsage,
  GeminiModelUsage,
} from '../types';
import { fetchPricingSettings } from './useSettings';
import { createLogger } from '../utils/logger';
import { apiFetch } from '../utils/api';

const log = createLogger('Stats');

/**
 * Fetch all jobs in a date range (handles pagination)
 */
export async function fetchAllJobsInRange(dateRange: DateRange): Promise<HistoricalJob[]> {
  const allJobs: HistoricalJob[] = [];
  let page = 1;
  const pageSize = 500;
  let hasMore = true;

  while (hasMore) {
    const params = new URLSearchParams();
    if (dateRange.from) {
      params.set('from_date', dateRange.from.toISOString());
    }
    if (dateRange.to) {
      params.set('to_date', dateRange.to.toISOString());
    }
    params.set('page', page.toString());
    params.set('page_size', pageSize.toString());

    const response = await apiFetch(`/api/jobs/history?${params.toString()}`);
    if (!response.ok) {
      log.error(`Failed to fetch jobs page ${page}: ${response.status}`);
      throw new Error(`Failed to fetch jobs: ${response.statusText}`);
    }

    const data = await response.json();
    allJobs.push(...data.jobs);

    if (page >= data.total_pages) {
      hasMore = false;
    } else {
      page++;
    }
  }

  log.info(`Fetched ${allJobs.length} jobs across ${page} pages for stats`);
  return allJobs;
}

/**
 * Calculate cost for a single job based on usage and pricing settings
 */
export function calculateJobCost(
  job: HistoricalJob,
  pricing: PricingSetting[]
): number {
  let cost = 0;

  // FAL usage (includes both FAL and OpenAI direct API calls)
  if (job.fal_usage) {
    for (const [endpoint, usage] of Object.entries(job.fal_usage)) {
      const typedUsage = usage as FalEndpointUsage;

      // Priority: 1) direct_cost from API (most accurate), 2) price_per_call estimate, 3) token-based pricing
      if (typedUsage.direct_cost && typedUsage.direct_cost > 0) {
        cost += typedUsage.direct_cost;
      } else {
        const price = pricing.find(
          (p) => p.endpoint_or_model === endpoint
        );
        if (price?.price_per_call && price.price_per_call > 0) {
          cost += typedUsage.calls * price.price_per_call;
        } else if (price) {
          cost += typedUsage.input_tokens * ((price.price_per_input_token || 0) / 1_000_000);
          cost += typedUsage.output_tokens * ((price.price_per_output_token || 0) / 1_000_000);
        }
      }
    }
  }

  // Gemini usage (prices stored as per-1M tokens)
  if (job.gemini_usage) {
    for (const [model, usage] of Object.entries(job.gemini_usage)) {
      const typedUsage = usage as GeminiModelUsage;
      const price = pricing.find(
        (p) => p.provider === 'gemini' && p.endpoint_or_model === model
      );
      if (price) {
        cost += typedUsage.input_tokens * ((price.price_per_input_token || 0) / 1_000_000);
        cost += typedUsage.output_tokens * ((price.price_per_output_token || 0) / 1_000_000);
      }
    }
  }

  return cost;
}

/**
 * Categorize an error message into a category
 * Only includes actual error types, not cancellations
 */
export function categorizeError(errorMessage: string | null): string {
  if (!errorMessage) return 'Unknown';
  
  const msg = errorMessage.toLowerCase();
  
  if (msg.includes('timeout') || msg.includes('timed out')) {
    return 'Timeout';
  }
  if (msg.includes('rate limit') || msg.includes('429') || msg.includes('quota')) {
    return 'Rate Limited';
  }
  if (msg.includes('invalid') || msg.includes('not found') || msg.includes('404')) {
    return 'Invalid Scan';
  }
  if (msg.includes('vertex') || msg.includes('gemini') || msg.includes('generation')) {
    return 'AI Generation Error';
  }
  if (msg.includes('api') || msg.includes('fetch') || msg.includes('network') || msg.includes('connection')) {
    return 'API Error';
  }
  
  return 'Other';
}

/**
 * Compute median from an array of numbers
 */
function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2;
}

/**
 * Create histogram buckets from data
 */
function createHistogram(
  values: number[],
  bucketCount: number = 10,
  formatLabel: (start: number, end: number) => string = (s, e) => `${s.toFixed(1)}-${e.toFixed(1)}`
): HistogramBucket[] {
  if (values.length === 0) return [];

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min;
  const bucketSize = range / bucketCount || 1;

  const buckets: HistogramBucket[] = [];
  for (let i = 0; i < bucketCount; i++) {
    const rangeStart = min + i * bucketSize;
    const rangeEnd = min + (i + 1) * bucketSize;
    buckets.push({
      range: formatLabel(rangeStart, rangeEnd),
      rangeStart,
      rangeEnd,
      count: 0,
    });
  }

  for (const value of values) {
    const bucketIndex = Math.min(
      Math.floor((value - min) / bucketSize),
      bucketCount - 1
    );
    if (bucketIndex >= 0 && bucketIndex < buckets.length) {
      buckets[bucketIndex].count++;
    }
  }

  return buckets;
}

/**
 * Format duration for histogram labels
 */
function formatDurationLabel(start: number, end: number): string {
  const formatSec = (s: number) => {
    if (s < 60) return `${Math.round(s)}s`;
    const m = Math.floor(s / 60);
    const sec = Math.round(s % 60);
    return sec > 0 ? `${m}m${sec}s` : `${m}m`;
  };
  return `${formatSec(start)}-${formatSec(end)}`;
}

/**
 * Format cost for histogram labels
 */
function formatCostLabel(start: number, end: number): string {
  return `$${start.toFixed(3)}-$${end.toFixed(3)}`;
}

/**
 * Compute duration statistics from jobs
 */
function computeDurationStats(jobs: HistoricalJob[]): DurationStats {
  const durations = jobs
    .filter((j) => j.status === 'completed' && j.duration_seconds != null)
    .map((j) => j.duration_seconds as number);

  if (durations.length === 0) {
    return {
      average: 0,
      median: 0,
      max: 0,
      min: 0,
      histogram: [],
    };
  }

  return {
    average: durations.reduce((a, b) => a + b, 0) / durations.length,
    median: median(durations),
    max: Math.max(...durations),
    min: Math.min(...durations),
    histogram: createHistogram(durations, 8, formatDurationLabel),
  };
}

/**
 * Compute cost statistics from jobs
 */
function computeCostStats(
  jobs: HistoricalJob[],
  pricing: PricingSetting[]
): CostStats {
  const completedJobs = jobs.filter((j) => j.status === 'completed');
  const costs = completedJobs.map((j) => calculateJobCost(j, pricing));
  const nonZeroCosts = costs.filter((c) => c > 0);

  if (nonZeroCosts.length === 0) {
    return {
      average: 0,
      median: 0,
      max: 0,
      min: 0,
      total: 0,
      histogram: [],
    };
  }

  const total = costs.reduce((a, b) => a + b, 0);

  return {
    average: total / completedJobs.length,
    median: median(nonZeroCosts),
    max: Math.max(...nonZeroCosts),
    min: Math.min(...nonZeroCosts),
    total,
    histogram: createHistogram(nonZeroCosts, 8, formatCostLabel),
  };
}

/**
 * Compute completion statistics from jobs
 * Success rate is calculated as completed / (completed + failed), excluding cancelled scans
 */
function computeCompletionStats(jobs: HistoricalJob[]): CompletionStats {
  const completed = jobs.filter((j) => j.status === 'completed').length;
  const failed = jobs.filter((j) => j.status === 'failed').length;
  const cancelled = jobs.filter((j) => j.status === 'cancelled').length;
  const total = jobs.length;
  
  // Success rate excludes cancelled scans - only considers completed vs failed
  const relevantTotal = completed + failed;

  return {
    completed,
    failed,
    cancelled,
    total,
    completedPercent: relevantTotal > 0 ? (completed / relevantTotal) * 100 : 0,
    failedPercent: relevantTotal > 0 ? (failed / relevantTotal) * 100 : 0,
    cancelledPercent: total > 0 ? (cancelled / total) * 100 : 0,
  };
}

/**
 * Compute error category statistics from failed jobs
 */
function computeErrorStats(jobs: HistoricalJob[]): ErrorCategory[] {
  const failedJobs = jobs.filter((j) => j.status === 'failed');
  if (failedJobs.length === 0) return [];

  const categoryCount: Record<string, number> = {};
  for (const job of failedJobs) {
    const category = categorizeError(job.error);
    categoryCount[category] = (categoryCount[category] || 0) + 1;
  }

  return Object.entries(categoryCount)
    .map(([category, count]) => ({
      category,
      count,
      percent: (count / failedJobs.length) * 100,
    }))
    .sort((a, b) => b.count - a.count);
}

/**
 * Compute per-endpoint usage statistics (success rate and average duration)
 */
function computeEndpointStats(jobs: HistoricalJob[]): EndpointStats[] {
  const endpointMap = new Map<
    string,
    {
      provider: 'fal' | 'gemini';
      totalCalls: number;
      jobsCompleted: number;
      jobsFailed: number;
      durationShareSum: number;
      durationShareCalls: number;
    }
  >();

  for (const job of jobs) {
    if (job.status === 'cancelled') continue;
    const isCompleted = job.status === 'completed';

    // Count total API calls in this job (for proportional duration split)
    let jobTotalCalls = 0;
    if (job.fal_usage) {
      for (const usage of Object.values(job.fal_usage)) {
        jobTotalCalls += (usage as FalEndpointUsage).calls;
      }
    }
    if (job.gemini_usage) {
      for (const usage of Object.values(job.gemini_usage)) {
        jobTotalCalls += (usage as GeminiModelUsage).calls;
      }
    }

    const recordEndpoint = (key: string, provider: 'fal' | 'gemini', calls: number) => {
      let entry = endpointMap.get(key);
      if (!entry) {
        entry = { provider, totalCalls: 0, jobsCompleted: 0, jobsFailed: 0, durationShareSum: 0, durationShareCalls: 0 };
        endpointMap.set(key, entry);
      }
      entry.totalCalls += calls;
      if (isCompleted) {
        entry.jobsCompleted++;
        if (job.duration_seconds != null && jobTotalCalls > 0) {
          // Attribute a proportional share of job duration to this endpoint
          const share = (calls / jobTotalCalls) * job.duration_seconds;
          entry.durationShareSum += share;
          entry.durationShareCalls += calls;
        }
      } else {
        entry.jobsFailed++;
      }
    };

    if (job.fal_usage) {
      for (const [endpoint, usage] of Object.entries(job.fal_usage)) {
        const typedUsage = usage as FalEndpointUsage;
        recordEndpoint(endpoint, 'fal', typedUsage.calls);
      }
    }

    if (job.gemini_usage) {
      for (const [model, usage] of Object.entries(job.gemini_usage)) {
        const typedUsage = usage as GeminiModelUsage;
        recordEndpoint(model, 'gemini', typedUsage.calls);
      }
    }
  }

  return Array.from(endpointMap.entries())
    .map(([endpoint, data]) => {
      const total = data.jobsCompleted + data.jobsFailed;
      return {
        endpoint,
        provider: data.provider,
        totalCalls: data.totalCalls,
        jobsCompleted: data.jobsCompleted,
        jobsFailed: data.jobsFailed,
        successRate: total > 0 ? (data.jobsCompleted / total) * 100 : 0,
        avgDuration: data.durationShareCalls > 0 ? data.durationShareSum / data.durationShareCalls : 0,
      };
    })
    .sort((a, b) => b.totalCalls - a.totalCalls);
}

/**
 * Compute all statistics for a set of jobs
 */
function computeAllStats(
  jobs: HistoricalJob[],
  pricing: PricingSetting[],
  dateRange: DateRange
): StatsData {
  // Get unique versions
  const versions = [
    ...new Set(
      jobs
        .map((j) => j.service_version)
        .filter((v): v is string => v != null && v !== '')
    ),
  ].sort();

  // Compute stats per version
  const versionStats: VersionStats[] = versions.map((version) => {
    const versionJobs = jobs.filter((j) => j.service_version === version);
    return {
      version,
      duration: computeDurationStats(versionJobs),
      cost: computeCostStats(versionJobs, pricing),
      completion: computeCompletionStats(versionJobs),
      errors: computeErrorStats(versionJobs),
      endpointStats: computeEndpointStats(versionJobs),
      jobCount: versionJobs.length,
    };
  });

  return {
    duration: computeDurationStats(jobs),
    cost: computeCostStats(jobs, pricing),
    completion: computeCompletionStats(jobs),
    errors: computeErrorStats(jobs),
    endpointStats: computeEndpointStats(jobs),
    versions,
    versionStats,
    totalJobs: jobs.length,
    dateRange,
  };
}

/**
 * Hook to fetch and compute statistics for a date range
 */
export function useStats(dateRange: DateRange) {
  const [stats, setStats] = useState<StatsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pricing, setPricing] = useState<PricingSetting[]>([]);

  // Fetch pricing settings on mount
  useEffect(() => {
    fetchPricingSettings()
      .then(setPricing)
      .catch((err) => log.error('Failed to load pricing settings:', err));
  }, []);

  // Fetch and compute stats when date range changes
  const fetchStats = useCallback(async () => {
    if (!dateRange.from && !dateRange.to) {
      setStats(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      log.debug('Computing stats for date range', dateRange.from?.toISOString(), '-', dateRange.to?.toISOString());
      const jobs = await fetchAllJobsInRange(dateRange);
      const computedStats = computeAllStats(jobs, pricing, dateRange);
      setStats(computedStats);
      log.info(`Stats computed: ${jobs.length} jobs, ${computedStats.completion.completed} completed, ${computedStats.completion.failed} failed`);
    } catch (err) {
      log.error('Failed to fetch statistics:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch statistics');
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [dateRange, pricing]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return {
    stats,
    loading,
    error,
    refetch: fetchStats,
  };
}

/**
 * Predefined date range options
 */
export type DateRangePreset = 'today' | 'last7days' | 'last30days' | 'custom';

export function getDateRangeFromPreset(preset: DateRangePreset): DateRange {
  const now = new Date();
  const endOfDay = new Date(now);
  endOfDay.setHours(23, 59, 59, 999);

  switch (preset) {
    case 'today': {
      const startOfDay = new Date(now);
      startOfDay.setHours(0, 0, 0, 0);
      return { from: startOfDay, to: endOfDay };
    }
    case 'last7days': {
      const start = new Date(now);
      start.setDate(start.getDate() - 7);
      start.setHours(0, 0, 0, 0);
      return { from: start, to: endOfDay };
    }
    case 'last30days': {
      const start = new Date(now);
      start.setDate(start.getDate() - 30);
      start.setHours(0, 0, 0, 0);
      return { from: start, to: endOfDay };
    }
    case 'custom':
    default:
      return { from: null, to: null };
  }
}
